import type {
  AdminPlanDiscountInput,
  AdminPlanPriceRuleInput,
  BillingApiGetAdminPlanProductsRequest,
  BillingApiGetAdminPlanProjectsRequest,
  BillingApiGetAdminPlansRequest,
  BillingApiGetClientInvoicePaymentAttemptsRequest,
  BillingApiGetClientInvoicesRequest,
  BillingApiGetCreditNotesRequest,
  BillingApiGetFinanceDashboardRequest,
  BillingApiGetInvoicePaymentAttemptsRequest,
  BillingApiGetInvoicesRequest,
  BillingApiGetSummariesRequest,
  BillingApiGetTransactionsRequest,
  BillingApiGetWalletTransactionsRequest,
  CreateAdminPlanRequest,
  CreateAdminProjectPlanRequest,
  CreatePaymentMethodRequest,
  CreateSummaryRequest,
  CreateWalletTransactionRequest,
  ExportAdminTransactionsRequest,
  ExportInvoiceTransactionsRequest,
  ExportTransactionsRequest,
  UpdateAdminPlanRequest,
  UpdateBillingProjectRequest,
  UpdateClientPlanRequest,
  UpdateClientProjectRequest,
  UpdatePaymentMethodRequest,
  UpdateWalletRequest,
} from '@teemill/billing';
import type { MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';
import { throwParsedAxiosBlobError } from '../utils';
import { client } from './plugin';
import { useBillingStore } from './store';

export const billingClient = client;

export const useBilling = (project: MaybeRefOrGetter<string>) => {
  return {
    store: useBillingStore(),
    overview: {
      get: () => client.getOverview({ project: toValue(project) }),
    },
    project: {
      get: () => client.getBillingProject({ project: toValue(project) }),
      update: (data: UpdateBillingProjectRequest) =>
        client.updateBillingProject({
          updateBillingProjectRequest: data,
          project: toValue(project),
        }),
    },
    clients: {
      list: (clientIds?: string[]) =>
        client.listClientProjects({
          project: toValue(project),
          ...(clientIds?.length ? { clients: clientIds } : {}),
        }),
      project: {
        get: (clientId: string) =>
          client.getClientProject({ project: toValue(project), client: clientId }),
        update: (clientId: string, data: UpdateClientProjectRequest) =>
          client.updateClientProject({
            project: toValue(project),
            client: clientId,
            updateClientProjectRequest: data,
          }),
      },
      plan: {
        get: (clientId: string) =>
          client.getClientPlan({ project: toValue(project), client: clientId }),
        update: (clientId: string, data: UpdateClientPlanRequest) =>
          client.updateClientPlan({
            project: toValue(project),
            client: clientId,
            updateClientPlanRequest: data,
          }),
      },
      wallet: {
        transactions: {
          create: (clientId: string, walletId: string, data: CreateWalletTransactionRequest) =>
            client.createWalletTransaction({
              project: toValue(project),
              client: clientId,
              id: walletId,
              createWalletTransactionRequest: data,
            }),
        },
      },
      paymentMethods: {
        list: (clientId: string) =>
          client.getClientPaymentMethods({ project: toValue(project), client: clientId }),
      },
      invoices: {
        list: (
          clientId: string,
          data: Omit<BillingApiGetClientInvoicesRequest, 'project' | 'client'>,
        ) => client.getClientInvoices({ ...data, project: toValue(project), client: clientId }),
        paymentAttempts: {
          list: (
            clientId: string,
            data: Omit<BillingApiGetClientInvoicePaymentAttemptsRequest, 'project' | 'client'>,
          ) =>
            client.getClientInvoicePaymentAttempts({
              ...data,
              project: toValue(project),
              client: clientId,
            }),
        },
        export: (data: ExportTransactionsRequest) =>
          client
            .exportClientInvoices(
              { exportTransactionsRequest: data, project: toValue(project) },
              { responseType: 'blob' },
            )
            .catch(throwParsedAxiosBlobError),
      },
    },
    transactions: {
      get: (transaction: string) =>
        client.getTransaction({ id: transaction, project: toValue(project) }),
      list: (data: Omit<BillingApiGetTransactionsRequest, 'project'>) =>
        client.getTransactions({ ...data, project: toValue(project) }),
      export: (data: ExportTransactionsRequest) =>
        client
          .exportTransactions(
            { exportTransactionsRequest: data, project: toValue(project) },
            {
              responseType: 'blob',
            },
          )
          .catch(throwParsedAxiosBlobError),
    },
    invoices: {
      get: (invoice: string) => client.getInvoice({ project: toValue(project), id: invoice }),
      list: (data: Omit<BillingApiGetInvoicesRequest, 'project'>) =>
        client.getInvoices({ ...data, project: toValue(project) }),
      download: (invoice: string) =>
        client
          .downloadInvoice(
            { id: invoice, project: toValue(project) },
            {
              responseType: 'blob',
            },
          )
          .catch(throwParsedAxiosBlobError),
      transactions: {
        export: (invoice: string, data: ExportInvoiceTransactionsRequest) =>
          client.exportInvoiceTransactions({
            id: invoice,
            project: toValue(project),
            exportInvoiceTransactionsRequest: data,
          }),
      },
      paymentAttempts: {
        list: (data: Omit<BillingApiGetInvoicePaymentAttemptsRequest, 'project'>) =>
          client.getInvoicePaymentAttempts({ ...data, project: toValue(project) }),
      },
    },
    creditNotes: {
      get: (creditNote: string) =>
        client.getCreditNote({ project: toValue(project), id: creditNote }),
      list: (data: Omit<BillingApiGetCreditNotesRequest, 'project'> = {}) =>
        client.getCreditNotes({ ...data, project: toValue(project) }),
      download: (creditNote: string) =>
        client
          .downloadCreditNote(
            { id: creditNote, project: toValue(project) },
            {
              responseType: 'blob',
            },
          )
          .catch(throwParsedAxiosBlobError),
    },
    summaries: {
      list: (data: Omit<BillingApiGetSummariesRequest, 'project'>) =>
        client.getSummaries({ ...data, project: toValue(project) }),
      get: (summary: string) => client.getSummary({ project: toValue(project), id: summary }),
      create: (data: Omit<CreateSummaryRequest, 'project'>) => {
        return client.createSummary({ createSummaryRequest: data, project: toValue(project) });
      },
      download: (summary: string) =>
        client
          .downloadSummary(
            { project: toValue(project), id: summary },
            {
              responseType: 'blob',
            },
          )
          .catch(throwParsedAxiosBlobError),
      transactions: {
        export: (summary: string, data: ExportInvoiceTransactionsRequest) => {
          return client.exportSummaryTransactions({
            project: toValue(project),
            id: summary,
            exportInvoiceTransactionsRequest: data,
          });
        },
      },
      delete: (summary: string) => client.deleteSummary({ project: toValue(project), id: summary }),
    },
    paymentMethods: {
      list: () => client.getPaymentMethods({ project: toValue(project) }),
      create: (data: CreatePaymentMethodRequest) =>
        client.createPaymentMethod({ createPaymentMethodRequest: data, project: toValue(project) }),
      update: (paymentMethod: string, data: UpdatePaymentMethodRequest) =>
        client.updatePaymentMethod({
          updatePaymentMethodRequest: data,
          project: toValue(project),
          id: paymentMethod,
        }),
      delete: (paymentMethod: string) =>
        client.deletePaymentMethod({ project: toValue(project), id: paymentMethod }),
      vendorSecret: {
        get: (vendor: string) =>
          client.getPaymentMethodVendorSecret({ project: toValue(project), vendor }),
      },
    },
    wallet: {
      update: (wallet: string, data: UpdateWalletRequest) =>
        client.updateWallet({ project: toValue(project), id: wallet, updateWalletRequest: data }),
      transactions: {
        list: (data: Omit<BillingApiGetWalletTransactionsRequest, 'project'>) =>
          client.getWalletTransactions({ ...data, project: toValue(project) }),
      },
    },
    plans: {
      list: () => client.getPlans({ project: toValue(project) }),
    },
    admin: {
      transactions: {
        export: (data: ExportAdminTransactionsRequest) =>
          client
            .exportAdminTransactions(
              {
                exportAdminTransactionsRequest: data,
                project: toValue(project),
              },
              {
                responseType: 'blob',
              },
            )
            .catch(throwParsedAxiosBlobError),
      },
      finance: {
        dashboard: {
          get: (data: Omit<BillingApiGetFinanceDashboardRequest, 'project'> = {}) =>
            client.getFinanceDashboard({
              ...data,
              project: toValue(project),
            }),
        },
      },
      plans: {
        list: (data: Omit<BillingApiGetAdminPlansRequest, 'project'> = {}) =>
          client.getAdminPlans({ ...data, project: toValue(project) }),
        get: (plan: string) => client.getAdminPlan({ project: toValue(project), plan }),
        create: (data: CreateAdminPlanRequest) =>
          client.createAdminPlan({
            project: toValue(project),
            createAdminPlanRequest: data,
          }),
        rename: (plan: string, data: UpdateAdminPlanRequest) =>
          client.updateAdminPlan({
            project: toValue(project),
            plan,
            updateAdminPlanRequest: data,
          }),
        delete: (plan: string): Promise<void> =>
          client.deleteAdminPlan({ project: toValue(project), plan }).then(() => undefined),
        products: {
          list: (
            plan: string,
            data: Omit<BillingApiGetAdminPlanProductsRequest, 'project' | 'plan'> = {},
          ) => client.getAdminPlanProducts({ ...data, project: toValue(project), plan }),
          get: (plan: string, sku: string) =>
            client.getAdminPlanProduct({ project: toValue(project), plan, sku }),
        },
        projects: {
          list: (
            plan: string,
            data: Omit<BillingApiGetAdminPlanProjectsRequest, 'project' | 'plan'> = {},
          ) => client.getAdminPlanProjects({ ...data, project: toValue(project), plan }),
        },
        priceRules: {
          update: (plan: string, sku: string, data: AdminPlanPriceRuleInput) =>
            client.updateAdminPlanPriceRule({
              project: toValue(project),
              plan,
              sku,
              adminPlanPriceRuleInput: data,
            }),
          delete: (plan: string, sku: string) =>
            client.deleteAdminPlanPriceRule({ project: toValue(project), plan, sku }),
        },
        discounts: {
          update: (plan: string, sku: string, data: AdminPlanDiscountInput) =>
            client.updateAdminPlanDiscount({
              project: toValue(project),
              plan,
              sku,
              adminPlanDiscountInput: data,
            }),
          delete: (plan: string, sku: string) =>
            client.deleteAdminPlanDiscount({ project: toValue(project), plan, sku }),
        },
      },
      projects: {
        plan: {
          create: (projectCode: string, data: CreateAdminProjectPlanRequest) =>
            client.createAdminProjectPlan({
              project: toValue(project),
              projectCode,
              createAdminProjectPlanRequest: data,
            }),
          revert: (projectCode: string) =>
            client.deleteAdminProjectPlan({ project: toValue(project), projectCode }),
        },
      },
    },
  };
};
